import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ApiRequest,
  ApiResponse,
  JsonHeader,
  ApiError,
  ApiReportRequest,
} from '../../models/api.model';
import { I18nService } from '../common/i18n.service';
import { Store } from '@ngrx/store';
import * as AuthSelectors from '@app/store/auth/auth.selectors';
import { AuthUser } from '../../../pages/auth/models/auth.model';
import { NotificationService } from '../common/notification.service';
import { EnvService } from '../common/env.service';

export interface ApiCallbacks<T = unknown> {
  onSuccess?: (data: T) => void;
  onError?: (error: ApiError) => void;
}

/**
 * Base API Service
 */
@Injectable({ providedIn: 'root' })
export class BaseApiService {
  protected readonly envService = inject(EnvService);
  protected readonly http = inject(HttpClient);
  protected readonly i18n = inject(I18nService);
  protected readonly notification = inject(NotificationService);
  protected readonly store = inject(Store);

  user$ = this.store.select(AuthSelectors.selectUser);
  protected currentUser: AuthUser | null = null;

  constructor() {
    this.user$.subscribe((user) => (this.currentUser = user));
  }

  /**
   * Get business API URL - computed dynamically từ env config
   * Tính toán mỗi lần gọi để đảm bảo lấy config mới nhất
   */
  private getBusinessApiUrl(): string {
    return `${this.envService.getApiUrl()}/api/business/private`;
  }

  /**
   * Get clear cache API URL - computed dynamically từ env config
   * Tính toán mỗi lần gọi để đảm bảo lấy config mới nhất
   */
  private getClearCacheApiUrl(): string {
    return `${this.envService.getApiUrl()}/api/business/clear-cache`;
  }

  /**
   * Execute business API POST request
   */
  public executePostRequest<TResponse = any, TRequest = any>(
    bizType: string,
    jsonParam: TRequest = {} as TRequest,
    headerOverrides?: Partial<JsonHeader>,
    onSuccess?: (data: TResponse) => void,
    onError?: (error: ApiError) => void,
    throwOnError: boolean = true,
  ): Observable<ApiResponse<TResponse>> {
    return this.executeApiCall<TRequest, TResponse>(
      'POST',
      bizType,
      jsonParam,
      headerOverrides,
      onSuccess,
      onError,
      throwOnError,
    );
  }

  /**
   * Core API execution method
   */
  private executeApiCall<TRequest, TResponse>(
    _method: 'POST' | 'GET' | 'DELETE',
    bizType: string,
    jsonParam: TRequest,
    headerOverrides?: Partial<JsonHeader>,
    onSuccess?: (data: TResponse) => void,
    onError?: (error: ApiError) => void,
    throwOnError: boolean = true,
  ): Observable<ApiResponse<TResponse>> {
    const request: ApiRequest<TRequest> = {
      bizType,
      JsonHeader: this.createHeader(headerOverrides),
      jsonParam,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });

    // Lấy URL mỗi lần gọi API để đảm bảo config mới nhất
    const businessApiUrl = this.getBusinessApiUrl();
    return this.http
      .post<ApiResponse<TResponse>>(businessApiUrl, request, {
        headers,
      })
      .pipe(
        map((response) => this.handleResponse(response, onSuccess, onError, throwOnError)),
        catchError((error) => this.handleError(error, onError)),
      );
  }

  /**
   * Clear cache
   */
  public clearCache(): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });

    // Lấy URL mỗi lần gọi API để đảm bảo config mới nhất
    const clearCacheApiUrl = this.getClearCacheApiUrl();

    return this.http.post(clearCacheApiUrl, {}, { headers });
  }

  /**
   * Execute download API request (returns stream/blob)
   */
  public executeDownloadRequest<TRequest, TResponse = Blob>(
    bizType: string,
    fileType: string,
    fileName: string,
    folder: string,
    jsonParam: TRequest,
    headerOverrides?: Partial<JsonHeader>,
    onSuccess?: (data: TResponse) => void,
    onError?: (error: ApiError) => void,
  ): Observable<any> {
    const request: ApiReportRequest<TRequest> = {
      bizType,
      JsonHeader: this.createHeader(headerOverrides),
      fileType,
      fileDownload: 'stream',
      fileName,
      folder,
      jsonParam,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const downloadApiUrl = `${this.envService.getApiUrl()}/api/download/excel`;

    return this.http
      .post(downloadApiUrl, request, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          if (onSuccess && response.body) {
            onSuccess(response.body as TResponse);
          }
          return response;
        }),
        catchError((error) => this.handleError(error, onError)),
      );
  }

  /**
   * Create header
   */
  private createHeader(overrides?: Partial<JsonHeader>): JsonHeader {
    return {
      lang: this.i18n.getCurrentLang(),
      user: this.currentUser?.LoginName ?? '',
      channel: 'BACK',
      branchCode: this.currentUser?.branchCode ?? '',
      branchId: this.currentUser?.branchId ?? 0,
      ...(overrides || {}),
    };
  }

  /**
   * Handle response
   */
  private handleResponse<T>(
    response: ApiResponse<T>,
    onSuccess?: (data: T) => void,
    onError?: (error: ApiError) => void,
    throwOnError: boolean = true,
  ): ApiResponse<T> {
    if (response.Rc && response.Rc !== 0) {
      const error: ApiError = {
        code: response.Rs || `RS_${response.Rs}`,
        message: response.RsMsg || this.i18n.translate('errors.general_failure'),
      };
      if (onError) {
        onError(error);
      } else if (throwOnError) {
        // Chỉ tự động hiển thị error khi throwOnError = true
        this.notification.error(error.message);
      }
      if (throwOnError) {
        throw error;
      }
    }
    if (onSuccess) {
      onSuccess(response.Data);
    }

    return response;
  }

  /**
   * Handle error
   */
  public handleError(
    error: HttpErrorResponse,
    onError?: (error: ApiError) => void,
  ): Observable<never> {
    const apiError: ApiError =
      error.error instanceof ErrorEvent
        ? {
            code: 'CLIENT_ERROR',
            message: error.error.message,
            details: this.i18n.translate('errors.client_error'),
          }
        : {
            code: `HTTP_${error.status}`,
            message: error.message || this.i18n.translate('errors.server_error'),
            status: error.status,
          };
    onError?.(apiError);
    return throwError(() => apiError);
  }
}
